import { useConnection, useReadContracts } from "wagmi";
import { type Address } from "viem";
import { type ChainType } from "@/constants/chain-specific";
import { erc721Abi } from "viem";
import { useBatchNFTMetadata } from "@/hooks/useNFTMetadata";
import { useAddressTokenBalances } from "./useFetchTokenBalances";
import { useEffect, useMemo, useRef } from "react";
import { getCollectionByAddress } from "@/constants/marketplace/collections";
import { type NFTMetadata } from "@/util/nftMetadata";
import { useQuery } from "wagmi/query";

// Maximum number of NFTs to scan via ownerOf for non-enumerable collections.
// Scanning very large non-enumerable collections directly on-chain can be extremely slow
// and resource-intensive. It's better to rely on indexed data or more specific API endpoints.
const MAX_SCAN_LIMIT_FOR_NON_ENUMERABLE = 500;

export interface NFTToken {
  tokenId: bigint;
  contractAddress: Address;
  collectionName: string;
  metadata?: import('@/util/nftMetadata').NFTMetadata;
}

// Type for token instances stored in metadata
interface TokenInstance {
  id: string;
  holder_address_hash?: string;
  image_url?: string;
  animation_url?: string;
  metadata?: any;
  token_type: string;
  value: string;
}

// Extended ABI for enumerable NFT contracts
const erc721EnumerableAbi = [
  ...erc721Abi,
  {
    inputs: [{ name: 'owner', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { name: 'owner', type: 'address' },
      { name: 'index', type: 'uint256' }
    ],
    name: 'tokenOfOwnerByIndex',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'totalSupply',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const;

export const useNFTCollection = (
  contractAddress: Address | undefined,
  collectionName: string,
  chainId: ChainType,
  userAddress: Address | undefined,
  knownBalance?: number // Pre-verified balance from verification hook
) => {
  const { address } = useConnection();
  const targetAddress = userAddress || address;

  // Get token balances which now includes NFT collections with token instances
  const { data: tokenBalances } = useAddressTokenBalances(
    targetAddress || "",
    chainId
  );

  // Type assertion to fix TypeScript inference issue
  type UserBalances = {
    tokens: import('@/lib/blockscout-api').TokenBalance[] | null;
    nfts: {
      erc721: import('@/lib/blockscout-api').TokenBalance[];
      erc1155: import('@/lib/blockscout-api').TokenBalance[];
    };
  };
  const typedTokenBalances = tokenBalances as UserBalances | undefined;

  // Get collection config from marketplace whitelist
  const collection = contractAddress ? getCollectionByAddress(contractAddress, chainId) : undefined;

  // Determine if this is an ERC1155 collection
  // Check API response first, then fall back to marketplace config
  const isERC1155 = useMemo(() => {
    // First check the marketplace collection config (works for synthetic collections)
    if (collection?.type === 'ERC1155') {
      return true;
    }

    // Then check API response
    if (!typedTokenBalances || !contractAddress) return false;
    const allNfts = [...(typedTokenBalances.nfts.erc721 || []), ...(typedTokenBalances.nfts.erc1155 || [])];
    const apiCollection = allNfts.find(
      (nft) => nft.token.address?.toLowerCase() === contractAddress.toLowerCase()
    );
    return apiCollection?.token.type === "ERC-1155";
  }, [typedTokenBalances, contractAddress, collection?.type]);

  // Check if it's non-enumerable based on marketplace config
  const isNonEnumerable = collection?.noEnumerable || false;

  // Real-time balance checking using web3 contract calls
  // Skip RPC call if we have a known balance from the verification hook
  const { data: balanceData, refetch: refetchBalance } = useReadContracts({
    contracts: contractAddress && targetAddress ? [
      {
        address: contractAddress,
        abi: erc721EnumerableAbi,
        functionName: 'balanceOf',
        args: [targetAddress],
        chainId: chainId,
      }
    ] : [],
    query: {
      // Skip balance fetch if we already have a known balance
      enabled: !!contractAddress && !!targetAddress && knownBalance === undefined,
      staleTime: 1000 * 30, // 30 seconds
    },
  });

  // Use knownBalance if provided (from verification hook), otherwise use RPC result
  const rpcBalance = balanceData?.[0]?.result ? Number(balanceData[0].result) : 0;
  const currentBalance = knownBalance !== undefined && knownBalance > 0 ? knownBalance : rpcBalance;

  // Debug logging for balance resolution
  console.log(`🔢 Balance resolution for ${collectionName}:`, {
    contractAddress,
    knownBalance,
    rpcBalance,
    currentBalance,
    isNonEnumerable,
    isERC1155,
    hasMaxSupply: !!collection?.maxSupply,
    maxSupply: collection?.maxSupply
  });

  // Fetch token IDs using enumerable interface when balance > 0 (only for ERC721)
  // Skip this for non-enumerable collections
  const tokenIdContracts = contractAddress && targetAddress && currentBalance > 0 && !isERC1155 && !isNonEnumerable
    ? Array.from({ length: Math.min(currentBalance, 100) }, (_, index) => ({
      address: contractAddress,
      abi: erc721EnumerableAbi,
      functionName: 'tokenOfOwnerByIndex' as const,
      args: [targetAddress, BigInt(index)],
      chainId: chainId,
    }))
    : [];

  const { data: tokenIdsData, isLoading: isLoadingTokenIds } = useReadContracts({
    contracts: tokenIdContracts,
    query: {
      enabled: tokenIdContracts.length > 0,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  });

  // Non-enumerable token fetching - batch processing for large collections
  // This also serves as a fallback when enumerable interface isn't implemented
  // Trigger when: (explicitly non-enumerable) OR (we have a knownBalance and maxSupply for fallback scanning)
  const BATCH_SIZE = 50;

  // Determine if we should try non-enumerable scanning
  // This includes explicit non-enumerable collections AND collections where we can fallback scan
  // Use !! to ensure boolean result
  const shouldTryNonEnumerable = isNonEnumerable || (knownBalance !== undefined && knownBalance > 0 && !!collection?.maxSupply);

  const nonEnumerableContracts = useMemo(() => {
    if (!contractAddress || !targetAddress || !shouldTryNonEnumerable || !collection?.maxSupply) {
      return [];
    }

    // For very large collections, we might want to limit the initial scan
    // Cap the scan to avoid performance issues with extremely large non-enumerable collections.
    let maxToCheck = collection.maxSupply;
    if (collection.maxSupply > MAX_SCAN_LIMIT_FOR_NON_ENUMERABLE) {
      console.warn(
        `Collection ${collectionName} (${contractAddress}) has a max supply of ${collection.maxSupply}, ` +
        `which exceeds the scan limit of ${MAX_SCAN_LIMIT_FOR_NON_ENUMERABLE}. ` +
        `The displayed NFT list may be incomplete. Consider using backend indexing for this collection.`
      );
      maxToCheck = MAX_SCAN_LIMIT_FOR_NON_ENUMERABLE;
    }

    console.log(`🔍 Creating non-enumerable contracts for ${collectionName}:`, {
      maxToCheck,
      firstId: collection.firstId || 0,
      isExplicitlyNonEnumerable: isNonEnumerable,
      isFallbackScan: !isNonEnumerable && knownBalance !== undefined && knownBalance > 0
    });

    return Array.from({ length: maxToCheck }, (_, index) => ({
      address: contractAddress,
      abi: erc721Abi,
      functionName: 'ownerOf' as const,
      args: [BigInt(collection.firstId || 0) + BigInt(index)],
      chainId: chainId,
    }));
  }, [contractAddress, targetAddress, shouldTryNonEnumerable, collection?.maxSupply, collection?.firstId, chainId, collectionName, isNonEnumerable, knownBalance]);

  // Process in batches for better performance
  const { data: nonEnumerableData, isLoading: isLoadingNonEnumerable } = useReadContracts({
    contracts: nonEnumerableContracts,
    allowFailure: true,
    batchSize: BATCH_SIZE,
    query: {
      enabled: nonEnumerableContracts.length > 0,
      staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    },
  });

  // Process non-enumerable data separately
  // This runs for both explicit non-enumerable collections AND fallback scans
  const processedNonEnumerableTokenIds = useMemo(() => {
    // Only process if we're doing non-enumerable scanning (explicit or fallback)
    if (!shouldTryNonEnumerable) {
      return null;
    }

    if (!nonEnumerableData || nonEnumerableData.length === 0) {
      console.log(`⏳ Waiting for non-enumerable data for ${collectionName}...`);
      return null;
    }

    const tokenIds: bigint[] = [];
    const startId = BigInt(collection?.firstId || 0);
    let successCount = 0;
    let failureCount = 0;

    console.log(`🔍 Processing non-enumerable data for ${collectionName}:`, {
      dataLength: nonEnumerableData.length,
      startId: startId.toString(),
      targetAddress,
      maxSupply: collection?.maxSupply
    });

    for (let i = 0; i < nonEnumerableData.length; i++) {
      const result = nonEnumerableData[i];

      if (result.status === 'success') {
        successCount++;
        // Compare addresses in lowercase to avoid case sensitivity issues
        if (result.result && targetAddress && result.result.toLowerCase() === targetAddress.toLowerCase()) {
          tokenIds.push(startId + BigInt(i));
        }
      } else {
        failureCount++;
        // Token might not exist or contract might have reverted
        if (result.error && !result.error.message?.includes('nonexistent token')) {
          console.warn(`⚠️ Error checking token ${startId + BigInt(i)}:`, result.error);
        }
      }
    }

    console.log(`✅ Processed ${collectionName} ownership check:`, {
      totalChecked: nonEnumerableData.length,
      successfulChecks: successCount,
      failedChecks: failureCount,
      tokensFound: tokenIds.length,
      tokenIds: tokenIds.map(id => id.toString())
    });

    return tokenIds.length > 0 ? tokenIds.map(id => id.toString()) : [];
  }, [shouldTryNonEnumerable, nonEnumerableData, collection?.firstId, collection?.maxSupply, collectionName, targetAddress]);

  // Fallback method for enumerable contracts that fail (only for ERC721)
  const { data: fallbackData, isLoading: isLoadingFallback } = useQuery({
    queryKey: ['nftFallbackTokens', contractAddress, targetAddress, chainId, currentBalance, shouldTryNonEnumerable, collectionName],
    queryFn: async () => {
      if (!contractAddress || !targetAddress || isERC1155) return [];

      // For non-enumerable collections (explicit or fallback scan), we already processed the data above
      if (shouldTryNonEnumerable) {
        return [];
      }

      // Only use API fallback for enumerable collections
      if (currentBalance === 0) return [];

      try {
        // Try to get token IDs from our API endpoint
        const response = await fetch('/api/nft/balance', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userAddress: targetAddress,
            contractAddresses: [contractAddress],
            chainId: chainId,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          const result = data.results?.[0];
          if (result && result.tokenIds.length > 0) {
            return result.tokenIds.filter((id: string) => !id.startsWith('unknown_'));
          }
        }
      } catch (error) {
        console.warn('Fallback API failed:', error);
      }

      return [];
    },
    enabled: !!contractAddress && !!targetAddress && currentBalance > 0 && !isERC1155 && !shouldTryNonEnumerable &&
      (!tokenIdsData || tokenIdsData.every(result => !result.result)),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  // Extract token IDs and metadata from the stored token instances
  type ExtractedData = {
    balance: number;
    tokenIds: bigint[];
    tokenMetadata: Map<string, any>;
  };
  const { data: extractedData, isLoading: isBalanceLoading } = useQuery({
    queryKey: ['nftCollectionTokens', contractAddress, targetAddress, chainId, collectionName],
    queryFn: async () => {
      if (!contractAddress || !targetAddress || !typedTokenBalances) return null;

      try {
        // Find the NFT collection in the fetched data
        const allNfts = [...(typedTokenBalances.nfts.erc721 || []), ...(typedTokenBalances.nfts.erc1155 || [])];
        const collection = allNfts.find(
          (nft) => nft.token.address?.toLowerCase() === contractAddress.toLowerCase()
        );

        if (!collection) {
          console.log('No collection found for:', contractAddress);
          return {
            balance: 0,
            tokenIds: [],
            tokenMetadata: new Map(),
          };
        }

        // For ERC1155, extract from token_instance.metadata.token_instances
        if (collection.token.type === "ERC-1155" && collection.token_instance?.metadata) {
          console.log('🔍 Processing ERC1155 collection:', contractAddress, collection);
          const metadata = collection.token_instance.metadata as any;
          if (metadata.token_instances && Array.isArray(metadata.token_instances)) {
            const tokenInstances = metadata.token_instances as TokenInstance[];
            console.log('📦 Found token instances:', tokenInstances.length, tokenInstances);
            const tokenIds = tokenInstances.map((instance: TokenInstance) => BigInt(instance.id));

            // Create metadata map from token instances
            const tokenMetadata = new Map();
            tokenInstances.forEach((instance: TokenInstance) => {
              console.log('🎯 Processing token instance:', instance.id, instance);
              if (instance.metadata) {
                // Process the metadata and ensure image URLs are properly formatted
                const processedMetadata = {
                  ...instance.metadata,
                  image: instance.image_url || instance.metadata.image_url || instance.metadata.image,
                  name: instance.metadata.name || `Token #${instance.id}`,
                  description: instance.metadata.description || '',
                  attributes: instance.metadata.attributes || [],
                };

                console.log('🖼️ Original image sources for token', instance.id, ':', {
                  instance_image_url: instance.image_url,
                  metadata_image_url: instance.metadata.image_url,
                  metadata_image: instance.metadata.image,
                  final_image: processedMetadata.image
                });

                // Convert IPFS URLs to HTTP
                if (processedMetadata.image && processedMetadata.image.startsWith('ipfs://')) {
                  processedMetadata.image = processedMetadata.image.replace('ipfs://', 'https://ipfs.chewy.build/ipfs/');
                  console.log('🔄 Converted IPFS URL for token', instance.id, ':', processedMetadata.image);
                }

                tokenMetadata.set(instance.id, processedMetadata);
                console.log('✅ Stored metadata for token', instance.id, ':', processedMetadata);
              } else {
                console.warn('⚠️ No metadata found for token instance:', instance.id);
              }
            });

            console.log('📊 Final ERC1155 extraction result:', {
              balance: tokenIds.length,
              tokenIds: tokenIds.map(id => id.toString()),
              metadataCount: tokenMetadata.size
            });

            return {
              balance: tokenIds.length,
              tokenIds,
              tokenMetadata,
            };
          } else {
            console.warn('⚠️ No token_instances array found in metadata for ERC1155:', metadata);
          }
        }

        // For ERC721, we'll rely on the metadata fetching hook
        return {
          balance: 0,
          tokenIds: [],
          tokenMetadata: new Map(),
        };
      } catch (error) {
        console.error('Error extracting NFT data:', error);
        return {
          balance: 0,
          tokenIds: [],
          tokenMetadata: new Map(),
        };
      }
    },
    enabled: !!contractAddress && !!targetAddress && !!typedTokenBalances,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Type assertions for query results
  const typedFallbackData = fallbackData as string[] | undefined;
  const typedExtractedData = extractedData as ExtractedData | null | undefined;

  // Determine the final token IDs to use
  const finalTokenIds = (() => {
    console.log(`🔍 Determining token IDs for ${collectionName}:`, {
      isERC1155,
      isNonEnumerable,
      shouldTryNonEnumerable,
      knownBalance,
      currentBalance,
      hasNonEnumerableData: !!processedNonEnumerableTokenIds && processedNonEnumerableTokenIds.length > 0,
      nonEnumerableDataLength: processedNonEnumerableTokenIds?.length || 0,
      hasFallbackData: !!typedFallbackData && typedFallbackData.length > 0,
      fallbackDataLength: typedFallbackData?.length || 0,
      hasTokenIdsData: !!tokenIdsData && tokenIdsData.some(result => result.result !== undefined),
      tokenIdsDataLength: tokenIdsData?.filter(r => r.result !== undefined).length || 0,
    });

    // Priority 1: For ERC1155, use extracted data
    if (isERC1155 && typedExtractedData && typedExtractedData.tokenIds.length > 0) {
      console.log('🎯 Using ERC1155 extracted token IDs');
      return typedExtractedData.tokenIds;
    }

    // Priority 2: For EXPLICITLY non-enumerable collections, use non-enumerable data
    if (isNonEnumerable && processedNonEnumerableTokenIds && processedNonEnumerableTokenIds.length > 0) {
      const tokenIds = processedNonEnumerableTokenIds.map(id => BigInt(id));
      console.log('🎯 Using non-enumerable token IDs (explicit):', processedNonEnumerableTokenIds);
      return tokenIds;
    }

    // Priority 3: Real-time enumerable data (ERC721) - preferred method for most collections
    if (tokenIdsData?.some(result => result.result !== undefined)) {
      const validTokenIds = tokenIdsData
        .filter(result => result.result !== undefined && result.result >= 0n) // Allow token ID 0
        .map(result => result.result as bigint);

      if (validTokenIds.length > 0) {
        console.log('🎯 Using enumerable token IDs:', validTokenIds.map((id: bigint) => id.toString()));
        return validTokenIds;
      }
    }

    // Priority 4: Fallback to non-enumerable scan when enumerable failed but we have maxSupply
    if (!isNonEnumerable && processedNonEnumerableTokenIds && processedNonEnumerableTokenIds.length > 0) {
      const tokenIds = processedNonEnumerableTokenIds.map(id => BigInt(id));
      console.log('🎯 Using non-enumerable token IDs (fallback scan):', processedNonEnumerableTokenIds);
      return tokenIds;
    }

    // Priority 5: Fallback API data
    if (typedFallbackData && typedFallbackData.length > 0) {
      const validTokenIds = typedFallbackData
        .map((id: string) => BigInt(id))
        .filter((id: bigint) => id >= 0n); // Allow token ID 0

      if (validTokenIds.length > 0) {
        console.log('🎯 Using fallback API token IDs:', validTokenIds.map((id: bigint) => id.toString()));
        return validTokenIds;
      }
    }

    // Priority 6: Generate placeholder IDs for small enumerable collections only
    if (currentBalance > 0 && !isERC1155 && !isNonEnumerable && currentBalance <= 10) {
      console.warn('⚠️ Using placeholder token IDs - this may cause errors if tokens don\'t exist');
      // Start from token ID 1 instead of 0, as many NFT contracts start from 1
      return Array.from({ length: Math.min(currentBalance, 10) }, (_, i) => BigInt(i + 1));
    }

    return [];
  })();

  // Create NFT tokens array
  const tokens: NFTToken[] = finalTokenIds.map((tokenId: bigint) => ({
    tokenId,
    contractAddress: contractAddress!,
    collectionName,
  }));

  // Debug logging for token creation
  if (tokens.length > 0) {
    console.log(`📋 useNFTCollection created ${tokens.length} tokens for ${contractAddress}:`, {
      collectionName,
      tokenIds: tokens.map(t => t.tokenId.toString()),
      contractAddress,
      chainId
    });
  }

  console.log(`📋 Created ${tokens.length} tokens for collection ${contractAddress}:`, {
    isERC1155,
    currentBalance,
    finalTokenIds: finalTokenIds.map((id: bigint) => id.toString()),
    hasEnumerableData: !!(tokenIdsData && tokenIdsData.some(result => result.result)),
    hasFallbackData: !!(typedFallbackData && typedFallbackData.length > 0),
    hasExtractedData: !!(typedExtractedData && typedExtractedData.tokenIds.length > 0)
  });

  // Create a metadata cache using useRef to persist across renders
  const metadataCache = useRef(new Map<string, NFTMetadata>());
  const processedTokenIds = useRef(new Set<string>());

  // Get metadata for the tokens (only for ERC721 or when ERC1155 doesn't have cached metadata)
  const { metadataMap } = useBatchNFTMetadata(
    contractAddress,
    finalTokenIds,
    chainId,
    {
      enabled: finalTokenIds.length > 0 &&
        (!isERC1155 || !typedExtractedData?.tokenMetadata?.size) &&
        // Don't fetch metadata if we're using placeholder IDs for large collections
        !(currentBalance > 10 && !isERC1155 && (!tokenIdsData || !tokenIdsData.some(result => result.result))),
      // Increased batchSize since NFT metadata fetching doesn't go through rate limiter
      batchSize: 20,
      isERC1155,
    }
  );

  // Update cache when metadataMap changes
  useEffect(() => {
    metadataMap.forEach((metadata, tokenId) => {
      if (!processedTokenIds.current.has(tokenId)) {
        metadataCache.current.set(tokenId, metadata);
        processedTokenIds.current.add(tokenId);
      }
    });
  }, [metadataMap]);

  // Combine tokens with their metadata
  const tokensWithMetadata = useMemo(() => {
    return tokens.map((token) => {
      const tokenIdStr = token.tokenId.toString();
      let metadata;

      // For ERC1155, prefer cached metadata from token instances
      if (isERC1155 && typedExtractedData?.tokenMetadata?.has(tokenIdStr)) {
        metadata = typedExtractedData.tokenMetadata.get(tokenIdStr);
      } else {
        // Try cache first, then fallback to metadataMap
        metadata = metadataCache.current.get(tokenIdStr) || metadataMap.get(tokenIdStr);
      }

      return {
        ...token,
        metadata: metadata || undefined,
      };
    });
  }, [tokens, isERC1155, typedExtractedData?.tokenMetadata, metadataMap]);

  const isLoading = isBalanceLoading || isLoadingTokenIds || isLoadingFallback || isLoadingNonEnumerable;
  const balance = finalTokenIds.length;


  return {
    tokens: tokensWithMetadata,
    balance,
    isLoading,
    currentBalance, // Real-time balance from contract
    refetch: refetchBalance,
  };
};
